import React, { useEffect, useMemo } from "react";
import { ThemeProvider } from "@mui/material";
import { getTheme } from "./themeOverrides";
import { useColorMode } from "./contexts/ColorModeContext";

const STYLE_ID = "dark-textfield-contrast-overrides";

/**
 * Injecte un <style> en fin de <head> pour que les surcharges TextField
 * passent après tous les styles MUI (spécificité + ordre dans le DOM).
 */
function injectDarkTextFieldStyles(mode: string): void {
  const existing = document.getElementById(STYLE_ID);
  if (existing) existing.remove();

  if (mode !== "dark") return;

  const focusLabelColor = "#fff";
  const focusBorderColor = "rgba(255,255,255,0.95)";

  const css = `
    html[data-theme="dark"] body .MuiTextField-root .MuiInputLabel-root.MuiFormLabel-colorPrimary { color: #fff !important; }
    html[data-theme="dark"] body .MuiTextField-root .MuiInputLabel-root.MuiFormLabel-colorPrimary.Mui-focused { color: ${focusLabelColor} !important; }
    html[data-theme="dark"] body .MuiTextField-root .MuiInputLabel-root.Mui-error { color: #fff !important; }
    html[data-theme="dark"] body .MuiTextField-root .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline { border-color: rgba(255,255,255,0.75) !important; }
    html[data-theme="dark"] body .MuiTextField-root .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color: rgba(255,255,255,0.9) !important; }
    html[data-theme="dark"] body .MuiTextField-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color: ${focusBorderColor} !important; }
    html[data-theme="dark"] body .MuiTextField-root .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline { border-color: rgba(255,255,255,0.75) !important; }
    html[data-theme="dark"] body .MuiTextField-root .MuiFormHelperText-root { color: rgba(255,255,255,0.78) !important; }
  `.trim();

  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.setAttribute("data-source", "ThemeWithMode-injectDarkTextFieldStyles");
  style.textContent = css;
  document.head.appendChild(style);
}

/**
 * Fournit le thème MUI en fonction du mode clair/sombre (contexte ColorMode).
 */
const ThemeWithMode: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { mode } = useColorMode();
  const theme = useMemo(() => getTheme(mode), [mode]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  useEffect(() => {
    injectDarkTextFieldStyles(mode);
    return () => {
      document.getElementById(STYLE_ID)?.remove();
    };
  }, [mode]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default ThemeWithMode;
